import { useMemo } from 'react';
import type { OrthographicViewport } from '@deck.gl/core';
import { FileText, Network, Layers } from 'lucide-react';
import type { AtlasDocument } from '@/api/knowledgeAtlas';
import { cn } from '@/lib/utils';

/**
 * Semantic-zoom LOD: when the user zooms IN past the card threshold, the small
 * landmark dots cross-fade into elegant, light INFO CARDS (Figma / Linear
 * style) showing each document's title, type, chunk count and relationship
 * count. These are HTML overlays positioned via deck's project(), which gives
 * the cleanest crisp-card look — so they MUST be culled (off-screen cards are
 * dropped) and CAPPED (top-N by degree within the viewport) to keep perf fine
 * and the canvas uncluttered.
 *
 * The downtown core (capital) card reads as the most prominent.
 */

// --- Tunable card tokens (mirrored from VIS.card in KnowledgeAtlas.tsx) ---
export interface CardTunables {
  /** Max cards rendered at once (top-N by degree within the viewport). */
  maxCards: number;
  /** Card width in px (the core card is a touch wider). */
  width: number;
  coreWidth: number;
  /** World-px margin around the viewport for culling (keep just-off-screen). */
  cullMarginPx: number;
}

interface DocumentCardsProps {
  documents: AtlasDocument[];
  /** Current deck viewport for the atlas view, used to project() world -> px. */
  viewport: OrthographicViewport | null;
  /** Card LOD opacity (0..1). When ~0 nothing renders. */
  opacity: number;
  /** The downtown-core document id (rendered most prominent), or null. */
  capitalId: number | null;
  /** Documents to emphasize (focus + neighbors / spotlight), or null = all normal. */
  illuminated: Set<number> | null;
  /** Search hits to mark with a civic-gold accent. */
  highlightedIds: Set<number>;
  tunables: CardTunables;
  onHover: (id: number | null) => void;
  onClick: (id: number) => void;
}

interface PlacedCard {
  doc: AtlasDocument;
  left: number;
  top: number;
  isCore: boolean;
}

export function DocumentCards({
  documents,
  viewport,
  opacity,
  capitalId,
  illuminated,
  highlightedIds,
  tunables,
  onHover,
  onClick,
}: DocumentCardsProps) {
  // Project every document, cull to (a padded) viewport, then cap to the
  // top-N by degree so a dense corpus never floods the screen. The core card
  // is always kept if it survives culling.
  const placed = useMemo<PlacedCard[]>(() => {
    if (!viewport || opacity <= 0.01) return [];
    const w = viewport.width;
    const h = viewport.height;
    const m = tunables.cullMarginPx;

    const visible: { doc: AtlasDocument; left: number; top: number }[] = [];
    for (const doc of documents) {
      const projected = viewport.project([doc.x, doc.y]);
      const px = projected[0];
      const py = projected[1];
      // Cull off-screen (with a small margin so cards entering frame appear early).
      if (px < -m || px > w + m || py < -m || py > h + m) continue;
      visible.push({ doc, left: px, top: py });
    }

    // Cap: keep the highest-degree landmarks in view (the meaningful ones),
    // always retaining the core if it's visible.
    if (visible.length > tunables.maxCards) {
      visible.sort((a, b) => degreeOf(b.doc) - degreeOf(a.doc));
      const capped = visible.slice(0, tunables.maxCards);
      if (capitalId !== null && !capped.some((c) => c.doc.id === capitalId)) {
        const core = visible.find((c) => c.doc.id === capitalId);
        if (core) capped[capped.length - 1] = core;
      }
      return capped.map((c) => ({ ...c, isCore: c.doc.id === capitalId }));
    }
    return visible.map((c) => ({ ...c, isCore: c.doc.id === capitalId }));
  }, [documents, viewport, opacity, capitalId, tunables.maxCards, tunables.cullMarginPx]);

  if (placed.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-10" aria-hidden>
      {placed.map(({ doc, left, top, isCore }) => {
        const dimmed = illuminated !== null && !illuminated.has(doc.id);
        const lit = illuminated !== null && illuminated.has(doc.id);
        const isHit = highlightedIds.has(doc.id);
        const cardOpacity = opacity * (dimmed ? 0.35 : 1);
        return (
          <DocumentCard
            key={doc.id}
            doc={doc}
            left={left}
            top={top}
            isCore={isCore}
            lit={lit}
            isHit={isHit}
            opacity={cardOpacity}
            width={isCore ? tunables.coreWidth : tunables.width}
            onHover={onHover}
            onClick={onClick}
          />
        );
      })}
    </div>
  );
}

function DocumentCard({
  doc,
  left,
  top,
  isCore,
  lit,
  isHit,
  opacity,
  width,
  onHover,
  onClick,
}: {
  doc: AtlasDocument;
  left: number;
  top: number;
  isCore: boolean;
  lit: boolean;
  isHit: boolean;
  opacity: number;
  width: number;
  onHover: (id: number | null) => void;
  onClick: (id: number) => void;
}) {
  // The core card gets a heavier elevation + a civic accent strip; search hits
  // get a gold keyline; lit (focused) cards a slightly stronger border.
  const shadow = isCore
    ? '0 18px 48px -18px rgba(15,23,42,0.42)'
    : '0 12px 30px -16px rgba(15,23,42,0.28)';
  return (
    <div
      className={cn(
        'pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer select-none rounded-xl border bg-white text-left transition-[border-color,box-shadow] duration-150',
        isHit
          ? 'border-amber-300'
          : isCore
            ? 'border-slate-300'
            : lit
              ? 'border-slate-300'
              : 'border-slate-200',
        'hover:border-slate-400'
      )}
      style={{ left, top, width, opacity, boxShadow: shadow }}
      onMouseEnter={() => onHover(doc.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onClick(doc.id)}
    >
      {isCore && (
        <div className="h-1 rounded-t-xl bg-slate-700" aria-hidden />
      )}
      <div className={cn('px-3', isCore ? 'pb-2.5 pt-2' : 'py-2.5')}>
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'truncate text-[10px] font-medium uppercase tracking-[0.16em]',
              isCore ? 'text-slate-500' : 'text-slate-400'
            )}
          >
            {doc.docType || (isCore ? 'Downtown Core' : 'Document')}
          </span>
        </div>
        <div
          className={cn(
            'mt-0.5 truncate font-semibold leading-tight text-slate-900',
            isCore ? 'text-[14px]' : 'text-[13px]'
          )}
          title={doc.title}
        >
          {doc.title}
        </div>
        <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1 tabular-nums">
            <FileText className="h-3 w-3 text-slate-400" />
            {formatCount(doc.chunkCount)}
          </span>
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Network className="h-3 w-3 text-slate-400" />
            {formatCount(doc.relationshipCount)}
          </span>
          {doc.lastUpdated && (
            <span className="inline-flex items-center gap-1 truncate">
              <Layers className="h-3 w-3 text-slate-400" />
              <span className="truncate">{formatUpdated(doc.lastUpdated)}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function degreeOf(d: AtlasDocument): number {
  return d.degree || d.relationshipCount || d.chunkCount || 0;
}

function formatCount(n: number | undefined): string {
  if (!n) return '0';
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

function formatUpdated(raw: string): string {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}
